import { useEffect, useState } from "react";
import api from "../api/axios";
import Toast from "../components/Toast";
import { useToast } from "../hooks/useToast";
import UserForm from "../components/UserForm";
import SearchFilter from "../components/SearchFilter";
import BulkActions from "../components/BulkActions";
import ExportData from "../components/ExportData";
import StatusBadge from "../components/StatusBadge";
import { exportSingleRecord } from "../utils/pdfExport";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Pagination from '../components/Pagination';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

export default function Users() {
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('cards');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const { toasts, success, error: showError, removeToast } = useToast();

  // Pagination logic
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const [usersRes, bookingsRes] = await Promise.all([
        api.get("/users"),
        api.get("/bookings")
      ]);

      const userData = usersRes.data.data || usersRes.data || [];
      const bookings = bookingsRes.data.data || bookingsRes.data || [];

      const usersWithCompleted = userData.map(user => {
        const userBookings = bookings.filter(booking => booking.user_id === user.id);
        const completedBookings = userBookings.filter(booking => booking.status === 'completed');
        return {
          ...user,
          completed_bookings_count: completedBookings.length
        };
      });

      setUsers(usersWithCompleted);
      setFilteredUsers(usersWithCompleted);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch users');
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEdit = (user) => {
    setEditUser(user);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm_delete_user'))) {
      try {
        // Validate and sanitize ID to prevent SSRF
        if (!id || (typeof id !== 'number' && typeof id !== 'string')) {
          throw new Error('Invalid user ID');
        }
        // Additional validation for numeric ID
        const numericId = Number(id);
        if (isNaN(numericId) || numericId <= 0) {
          throw new Error('Invalid user ID format');
        }
        const sanitizedId = encodeURIComponent(String(numericId));
        await api.delete(`/users/${sanitizedId}`);
        success(t('user_deleted_success'));
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        showError(t('failed_delete_user'));
      }
    }
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditUser(null);
    fetchUsers();
  };

  const handleSearch = (searchTerm) => {
    const filtered = users.filter(user =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.phone && user.phone.includes(searchTerm))
    );
    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  const handleFilter = (filterValue) => {
    if (!filterValue) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => user.status === filterValue);
      setFilteredUsers(filtered);
    }
    setCurrentPage(1);
  };

  const handleBulkAction = async (action, selectedIds) => {
    if (action === 'delete') {
      if (window.confirm(`${t('delete')} ${selectedIds.length} ${t('selected')} ${t('users')}?`)) {
        try {
          const validIds = selectedIds.filter(id => {
            const numericId = Number(id);
            return !isNaN(numericId) && numericId > 0;
          });
          await Promise.all(validIds.map(id => {
            const sanitizedId = encodeURIComponent(String(Number(id)));
            return api.delete(`/users/${sanitizedId}`);
          }));
          success(`${validIds.length} ${t('users')} ${t('deleted_successfully')}!`);
          fetchUsers();
          setSelectedUsers([]);
        } catch (error) {
          console.error('Bulk delete failed:', error);
          showError(t('failed_delete_users'));
        }
      }
    } else if (action === 'activate') {
      try {
        const validIds = selectedIds.filter(id => {
          const numericId = Number(id);
          return !isNaN(numericId) && numericId > 0;
        });
        await Promise.all(validIds.map(id => {
          const sanitizedId = encodeURIComponent(String(Number(id)));
          return api.put(`/users/${sanitizedId}`, { status: 'active' });
        }));
        success(`${validIds.length} ${t('users')} ${t('activated_successfully')}!`);
        fetchUsers();
        setSelectedUsers([]);
      } catch (error) {
        console.error('Bulk activate failed:', error);
        showError(t('failed_activate_users'));
      }
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      const numericId = Number(id);
      if (isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid user ID');
      }
      const sanitizedId = encodeURIComponent(String(numericId));
      await api.put(`/users/${sanitizedId}`, { status });
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
    }
  };

  const bulkActions = [
    { key: 'delete', label: t('delete'), className: 'bg-red-500 text-white hover:bg-red-600' },
    { key: 'activate', label: t('activate'), className: 'bg-green-500 text-white hover:bg-green-600' }
  ];

  const statusFilters = [
    { value: 'active', label: t('active') },
    { value: 'inactive', label: t('inactive') }
  ];

  if (loading) {
    return (
      
        <div className={`flex items-center justify-center min-h-96 ${isDark ? 'bg-gray-900' : ''}`}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      
    );
  }

  if (error) {
    return (
      
        <div
          
          
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-8 text-center`}
        >
          <div
            
            
            
            className={`text-6xl mb-4 ${isDark ? 'text-gray-400' : 'text-red-500'}`}
          >
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <h2
            
            
            
            className={`text-xl font-semibold mb-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}
          >
            {t('error_loading_users')}
          </h2>
          <p
            
            
            
            className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
          >
            {error}
          </p>
          <button
            
            
            
            
            
            onClick={fetchUsers}
            className={`px-6 py-2 rounded-lg transition-colors ${isDark ? 'bg-gray-600 text-gray-100 hover:bg-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            {t('try_again')}
          </button>
        </div>
      
    );
  }

  return (
    
      <div
        
        
        className="space-y-6"
      >
        {/* Header */}
        <div
          
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6`}
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div
              
              
              
            >
              <h1
                
                
                
                className={`text-3xl font-bold ${isDark ? 'text-gray-100' : 'bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent'}`}
              >
                {t('users_management')}
              </h1>
              <p
                
                
                
                className={`mt-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
              >
                {t('manage_user_accounts')}
              </p>
            </div>
            <div
              
              
              
              className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto"
            >
              <div
                
                
              >
                <ExportData data={filteredUsers} filename="users" title={t('users_management_report')} includeStats={true} language={language} />
              </div>
              
                {selectedUsers.length > 0 && (
                  <button
                    
                    
                    
                    
                    
                    onClick={() => {
                      const selectedData = filteredUsers.filter(u => selectedUsers.includes(u.id));
                      const doc = new jsPDF();
                      doc.setFontSize(16);
                      doc.text(t('selected_users_export'), 20, 20);
                      doc.setFontSize(10);
                      doc.text(`${t('generated')}: ${new Date().toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US')}`, 20, 30);

                      const headers = [t('id'), t('name'), t('email'), t('phone'), t('status')];
                      const rows = selectedData.map(item => [
                        item.id || '-',
                        item.name || '-',
                        item.email || '-',
                        item.phone || '-',
                        item.status || '-'
                      ]);

                      autoTable(doc, {
                        head: [headers],
                        body: rows,
                        startY: 40
                      });

                      const fileName = selectedData.length === 1
                        ? `${selectedData[0].name.replace(/[^a-zA-Z0-9]/g, '_')}_${selectedData[0].id}_${new Date().toISOString().split('T')[0]}.pdf`
                        : `selected_${selectedData.length}_users_${new Date().toISOString().split('T')[0]}.pdf`;
                      doc.save(fileName);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm shadow-lg hover:shadow-xl text-white ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700' : 'bg-gradient-to-r from-blue-600 to-blue-700'
                      }`}
                  >
                    <i
                      
                      
                      className="fas fa-file-pdf mr-1"
                    ></i>
                    Export Selected ({selectedUsers.length})
                  </button>
                )}
              
              <button
                
                
                onClick={() => setShowForm(true)}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center text-white ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                  }`}
              >
                <i
                  
                  
                  className="fas fa-plus mr-2"
                ></i>
                {t('add_user')}
              </button>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div
          
          
          
          className="flex flex-col lg:flex-row gap-4"
        >
          <div
            className="flex-1"
            
          >
            <SearchFilter
              onSearch={handleSearch}
              onFilter={handleFilter}
              filters={statusFilters}
            />
          </div>
          <div
            
            
            
            className="flex items-center gap-2"
          >
            <span className="text-sm text-gray-600">{t('view')}:</span>
            <button
              
              
              onClick={() => setViewMode('cards')}
              className={`px-3 py-2 rounded-lg text-sm transition-all ${viewMode === 'cards'
                  ? `bg-gradient-to-r ${isDark ? 'from-gray-600 to-gray-700' : 'from-blue-600 to-blue-700'} text-white shadow-lg`
                  : `${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`
                }`}
            >
              <i className="fas fa-th-large mr-1"></i> {t('cards')}
            </button>
            <button
              
              
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 rounded-lg text-sm transition-all ${viewMode === 'table'
                  ? `bg-gradient-to-r ${isDark ? 'from-gray-600 to-gray-700' : 'from-blue-600 to-blue-700'} text-white shadow-lg`
                  : `${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`
                }`}
            >
              <i className="fas fa-table mr-1"></i> {t('table')}
            </button>
          </div>
        </div>

        <div
          
          
          
        >
          <BulkActions
            selectedItems={selectedUsers}
            onAction={handleBulkAction}
            actions={bulkActions}
          />
        </div>

        {/* Content */}
        
          {viewMode === 'cards' ? (
            // Cards View
            <div
              key="cards"
              
              
              
              
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {currentUsers.map((user, index) => {
                if (!user) return null;
                return (
                  <div
                    key={user.id}
                    
                  >
                    <div
                      
                      
                      className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6 hover:shadow-2xl transition-all duration-300 h-96 flex flex-col cursor-pointer will-change-transform animate-gpu`}
                    >
                      {/* Checkbox */}
                      <div className="flex justify-between items-start mb-4">
                        <input
                          
                          
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleUserSelect(user.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div
                          
                        >
                          <StatusBadge
                            currentStatus={user.status}
                            statusOptions={['active', 'inactive']}
                            onStatusChange={(newStatus) => handleStatusUpdate(user.id, newStatus)}
                          />
                        </div>
                      </div>

                      {/* Avatar */}
                      <div className="flex justify-center mb-4">
                        <div
                          
                          className="w-20 h-20 rounded-xl overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg"
                        >
                          {user.profile_photo && user.profile_photo !== 'null' && user.profile_photo.trim() !== '' ? (
                            <img
                              
                              
                              src={user.profile_photo}
                              alt={user.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            
                            
                            className={`w-full h-full flex items-center justify-center text-white text-2xl font-bold ${user.profile_photo && user.profile_photo !== 'null' && user.profile_photo.trim() !== '' ? 'hidden' : 'flex'}`}
                          >
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                        </div>
                      </div>

                      {/* User Info */}
                      <div
                        
                        
                        
                        className="text-center mb-4 flex-1"
                      >
                        <h3
                          
                          className={`text-lg font-bold mb-1 truncate ${isDark ? 'text-gray-100' : 'text-gray-900'}`}
                          title={user.name}
                        >
                          {user.name}
                        </h3>
                        <p
                          
                          
                          
                          className={`text-sm mb-2 truncate ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                          title={user.email}
                        >
                          {user.email}
                        </p>
                        <p
                          
                          
                          
                          className={`text-sm truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                        >
                          <i
                            
                            
                            className={`fas fa-phone mr-1 ${isDark ? 'text-gray-400' : ''}`}
                          ></i>
                          {user.phone || t('no_phone')}
                        </p>
                        <p
                          
                          
                          
                          className={`text-sm mt-2 ${isDark ? 'text-blue-400' : 'text-blue-600'} font-medium`}
                        >
                          <i
                            
                            
                            className="fas fa-calendar-check mr-1"
                          ></i>
                          {user.bookings_count || 0} {user.bookings_count === 1 ? 'booking' : 'bookings'}
                        </p>
                        <p
                          
                          
                          
                          className={`text-xs mt-1 ${isDark ? 'text-green-400' : 'text-green-600'} font-medium`}
                        >
                          <i
                            
                            
                            className="fas fa-check-circle mr-1"
                          ></i>
                          {user.completed_bookings_count || 0} selesai
                        </p>
                        <p
                          
                          
                          
                          className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                        >
                          ID: {user.id}
                        </p>
                      </div>

                      {/* Actions */}
                      <div
                        
                        
                        
                        className="flex gap-2"
                      >
                        <button
                          
                          
                          onClick={() => handleEdit(user)}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg text-white ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600' : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                            }`}
                        >
                          <i
                            
                            
                            className="fas fa-edit mr-1"
                          ></i>
                          {t('edit')}
                        </button>
                        <button
                          
                          
                          onClick={() => {
                            try {
                              exportSingleRecord(user, 'user', 'user', language);
                            } catch (error) {
                              console.error('Single export failed:', error);
                              showError('Export gagal: ' + error.message);
                            }
                          }}
                          className={`py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg text-white ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                            }`}
                          title="Export User Details"
                        >
                          <i
                            
                            
                            className="fas fa-file-pdf"
                          ></i>
                        </button>
                        <button
                          
                          
                          onClick={() => handleDelete(user.id)}
                          className="bg-gradient-to-r from-gray-500 to-gray-600 text-white py-2 px-3 rounded-lg text-sm font-medium hover:from-red-500 hover:to-red-600 transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          <i
                            
                            
                            className="fas fa-trash"
                          ></i>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Table View
            <div
              key="table"
              
              
              
              
              className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border overflow-hidden`}
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead
                    
                    
                    
                    className={`bg-gradient-to-r ${isDark ? 'from-gray-700 to-gray-600' : 'from-slate-50 to-blue-50'}`}
                  >
                    <tr>
                      <th className="px-6 py-4 text-left">
                        <input
                          
                          
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUsers(currentUsers.map(u => u.id));
                            } else {
                              setSelectedUsers([]);
                            }
                          }}
                          checked={selectedUsers.length === currentUsers.length && currentUsers.length > 0}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{t('user')}</th>
                      <th className={`hidden sm:table-cell px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{t('email')}</th>
                      <th className={`hidden md:table-cell px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{t('phone')}</th>
                      <th className={`hidden lg:table-cell px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>Total / Selesai</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{t('status')}</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {currentUsers.map((user, index) => {
                      if (!user) return null;
                      return (
                        <animated.tr
                          key={user.id}
                          
                          className={`transition-all duration-200 ${isDark
                              ? `hover:bg-gradient-to-r hover:from-gray-700 hover:to-gray-600 ${selectedUsers.includes(user.id) ? 'bg-gradient-to-r from-gray-700 to-gray-600' : index % 2 === 0 ? 'bg-gray-800/50' : 'bg-gray-700/50'}`
                              : `hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 ${selectedUsers.includes(user.id) ? 'bg-gradient-to-r from-blue-50 to-blue-100' : index % 2 === 0 ? 'bg-white/50' : 'bg-slate-50/50'}`
                            }`}
                        >
                          <td className="px-6 py-4">
                            <input
                              
                              
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => handleUserSelect(user.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div
                                
                                className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center mr-3 flex-shrink-0"
                              >
                                {user.profile_photo && user.profile_photo !== 'null' && user.profile_photo.trim() !== '' ? (
                                  <img
                                    
                                    
                                    src={user.profile_photo}
                                    alt={user.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const fallback = e.target.nextElementSibling;
                                      if (fallback) fallback.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div
                                  
                                  
                                  className="w-full h-full flex items-center justify-center text-blue-600"
                                  style={{ display: user.profile_photo && user.profile_photo !== 'null' && user.profile_photo.trim() !== '' ? 'none' : 'flex' }}
                                >
                                  <i className="fas fa-user text-lg"></i>
                                </div>
                              </div>
                              <div
                                
                                
                                
                              >
                                <div
                                  
                                  className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}
                                >
                                  {user.name}
                                </div>
                                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>ID: {user.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="hidden sm:table-cell px-6 py-4">
                            <span
                              
                              
                              
                              className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                            >
                              {user.email}
                            </span>
                          </td>
                          <td className="hidden md:table-cell px-6 py-4">
                            <span
                              
                              
                              
                              className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                            >
                              {user.phone || t('no_phone')}
                            </span>
                          </td>
                          <td className="hidden lg:table-cell px-6 py-4">
                            <div
                              
                              
                              
                              className="flex flex-col gap-1"
                            >
                              <span
                                
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${(user.bookings_count || 0) > 0
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                  }`}
                              >
                                <i
                                  
                                  
                                  className="fas fa-calendar-check mr-1"
                                ></i>
                                {user.bookings_count || 0} total
                              </span>
                              <span
                                
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              >
                                <i
                                  
                                  
                                  className="fas fa-check-circle mr-1"
                                ></i>
                                {user.completed_bookings_count || 0} selesai
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div
                              
                              
                              
                              
                            >
                              <StatusBadge
                                currentStatus={user.status}
                                statusOptions={['active', 'inactive']}
                                onStatusChange={(newStatus) => handleStatusUpdate(user.id, newStatus)}
                              />
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div
                              
                              
                              
                              className="flex gap-1"
                            >
                              <button
                                
                                
                                onClick={() => handleEdit(user)}
                                className={`px-2 py-1 rounded-lg text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md text-white ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600' : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                                  }`}
                              >
                                {t('edit')}
                              </button>
                              <button
                                
                                
                                onClick={() => {
                                  try {
                                    exportSingleRecord(user, 'user', 'user', language);
                                  } catch (error) {
                                    console.error('Single export failed:', error);
                                    showError('Export gagal: ' + error.message);
                                  }
                                }}
                                className={`px-2 py-1 rounded-lg text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md text-white ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                                  }`}
                              >
                                <i
                                  
                                  
                                  className="fas fa-file-pdf"
                                ></i>
                              </button>
                              <button
                                
                                
                                onClick={() => handleDelete(user.id)}
                                className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-2 py-1 rounded-lg text-xs font-medium hover:from-red-500 hover:to-red-600 transition-all duration-200 shadow-sm hover:shadow-md"
                              >
                                {t('delete')}
                              </button>
                            </div>
                          </td>
                        </animated.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        

        
          {filteredUsers.length === 0 && (
            <div
              
              
              
              className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-12 text-center`}
            >
              <div
                
                
                
                className={`text-6xl mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
              >
                <i className="fas fa-users"></i>
              </div>
              <h3
                
                
                
                className={`text-xl font-semibold mb-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}
              >
                {t('no_users_found')}
              </h3>
              <p
                
                
                
                className={isDark ? 'text-gray-300' : 'text-gray-600'}
              >
                {t('adjust_search_filter')}
              </p>
            </div>
          )}
        

        {/* Pagination */}
        {filteredUsers.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={filteredUsers.length}
          />
        )}

        
          {showForm && (
            <UserForm
              user={editUser}
              onSave={handleFormSave}
              onCancel={() => { setShowForm(false); setEditUser(null); }}
            />
          )}
        

        {/* Toast Notifications */}
        
          {toasts.map(toast => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              isVisible={true}
              onClose={() => removeToast(toast.id)}
              duration={toast.duration}
            />
          ))}
        
      </div>
    
  );
}